import { IafItemSvc } from '../../services/platformApi';
import type { PlatformContext } from '../../types/config';
import type { SimulationScenario, ScenariosCollection } from './types';
// Local JSON seeds (optional)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - Vite allows importing JSON
import bimEquipment from '../../scripts/bim_equipment.json';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - Vite allows importing JSON
import testScenario1 from '../../scripts/test_scenario_1_ahu_chiller.json';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - Vite allows importing JSON
import testScenario2 from '../../scripts/test_scenario_2_building_systems.json';

export const SCENARIOS_USER_TYPE = 'sim_scenarios';

/**
 * Decodes MongoDB Decimal128 objects to JavaScript numbers
 */
function decodeDecimal128({ high, low, negative }: { high: number; low: number; negative?: boolean }): number {
  const SIGN_BIT_MASK = 0x8000000000000000n;
  const COMBINATION_MASK = 0x7C00000000000000n;

  // Convert to BigInts
  const highBig = BigInt(high);
  const lowBig = BigInt(low);

  // Combine high and low into a full 128-bit value
  const full = (highBig << 64n) | lowBig;

  // Extract sign
  const isNegative = (highBig & SIGN_BIT_MASK) !== 0n;

  // Extract the combination field (bits 1-5 after sign)
  const comb = (highBig & COMBINATION_MASK) >> 58n;

  // Handle special values
  if (comb === 0x1fn) return NaN;
  if (comb === 0x1en) return isNegative ? -Infinity : Infinity;

  // Extract exponent (bits 1–14 after sign)
  const biasedExponent = Number((highBig >> 49n) & 0x3FFFn);
  const exponent = biasedExponent - 6176; // Decimal128 bias is 6176

  // Extract significand (coefficient)
  // We'll combine the last 49 bits of high + all 64 of low
  const significandHigh = highBig & 0x0001FFFFFFFFFFFFn;
  const significand = (significandHigh << 64n) | lowBig;

  // Now convert to a decimal using BigInt math and scaling
  const decimalValue = BigInt(significand.toString());

  // Convert to float (approximate!)
  const floatVal = Number(decimalValue) * 10 ** exponent;

  return isNegative ? -floatVal : floatVal;
}

/**
 * Check if an object is a Decimal128 object
 */
function isDecimal128(obj: any): boolean {
  if (typeof obj !== 'object' || obj === null || Array.isArray(obj)) {
    return false;
  }
  // Must have high and low as integers, and also have the Decimal128 metadata properties
  // Note: high can be larger than MAX_SAFE_INTEGER, so we use Number.isInteger instead
  return (
    'high' in obj &&
    'low' in obj &&
    typeof obj.high === 'number' &&
    typeof obj.low === 'number' &&
    Number.isInteger(obj.high) &&
    Number.isInteger(obj.low) &&
    'negative' in obj &&
    'finite' in obj
  );
}

/**
 * Deep recursively decodes any Decimal128 objects in a data structure
 */
function decodeDecimal128Deep(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj;
  }

  // Check if this is a Decimal128 object
  if (isDecimal128(obj)) {
    return decodeDecimal128(obj);
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map(item => decodeDecimal128Deep(item));
  }

  // Handle plain objects
  if (typeof obj === 'object') {
    const decoded: any = {};
    for (const [key, value] of Object.entries(obj)) {
      decoded[key] = decodeDecimal128Deep(value);
    }
    return decoded;
  }

  // Primitive values pass through unchanged
  return obj;
}

export async function getScenariosCollection(ctx: PlatformContext): Promise<ScenariosCollection | null> {
  try {
    const res = await IafItemSvc.getNamedUserItems(
      { query: { _userType: SCENARIOS_USER_TYPE } },
      ctx,
      { _pageSize: 1 }
    );
    return res._list?.[0] || null;
  } catch (e) {
    console.warn('getScenariosCollection failed', e);
    return null;
  }
}

export async function getAllScenariosCollections(ctx: PlatformContext): Promise<ScenariosCollection[]> {
  try {
    const res = await IafItemSvc.getNamedUserItems(
      { query: { _userType: SCENARIOS_USER_TYPE } },
      ctx,
      { _pageSize: 100 }
    );
    return res._list || [];
  } catch (e) {
    console.warn('getAllScenariosCollections failed', e);
    return [];
  }
}

export async function deleteCollection(collectionId: string, ctx: PlatformContext): Promise<void> {
  try {
    await IafItemSvc.deleteNamedUserItem(collectionId, ctx);
  } catch (e) {
    console.error('deleteCollection failed', e);
    throw e;
  }
}

export async function createScenariosCollection(ctx: PlatformContext, namespaces: string[], name = 'Simulation Scenarios'): Promise<ScenariosCollection | null> {
  try {
    // Using low-level REST similar to existing patterns in the app
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const baseUri = (await import('../../services/platformApi')).IafFetch.CONFIG.itemServiceOrigin;
    // Use ONLY the first namespace - project's primary namespace
    const ns = namespaces?.[0];
    // _shortName must be 1..22 chars. Use a compact, stable short name.
    const shortName = 'sim_scenarios';
    const payload = [{
      _itemClass: 'NamedUserCollection',
      _name: name,
      _shortName: shortName,
      _description: 'Simulation scenarios for equipment and building systems',
      _userType: SCENARIOS_USER_TYPE,
      _namespaces: ns ? [ns] : [], // Ensure only single namespace array
    }];
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const token = (ctx as any).authToken;
    const resp = await fetch(`${baseUri}/itemsvc/api/v1/nameduseritems?nsfilter=${ns}`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: token },
      body: JSON.stringify(payload),
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const body = await resp.json();
    const created = Array.isArray(body) ? body[0] : body._list?.[0] || body;
    if (created?._userItemId) return created;
    // Fallback: fetch
    return await getScenariosCollection(ctx);
  } catch (e) {
    console.error('createScenariosCollection failed', e);
    return null;
  }
}

export async function listScenarios(collection: ScenariosCollection, ctx: PlatformContext): Promise<SimulationScenario[]> {
  if (!collection?._userItemId) return [];
  try {
    const res = await IafItemSvc.getRelatedItems(
      collection._userItemId,
      {},
      ctx,
      { page: { _pageSize: 1000 } }
    );
    const list = res?._list || [];

    // Map and decode Decimal128 values
    const scenarios = list.map((it: any) => {
      const scenario = {
        _id: it._id,
        name: it.name || it._userAttributes?.name,
        description: it.description || it._userAttributes?.description || '',
        equipment: it.equipment || it._userAttributes?.equipment || [],
        sensorCorrelations: it.sensorCorrelations || it._userAttributes?.sensorCorrelations || [],
        equipmentInteractions: it.equipmentInteractions || it._userAttributes?.equipmentInteractions || [],
        createdAt: it.createdAt || it._userAttributes?.createdAt,
        updatedAt: it.updatedAt || it._userAttributes?.updatedAt,
        createdBy: it.createdBy || it._userAttributes?.createdBy,
      };

      // Deep decode all Decimal128 values in equipment, correlations, and interactions
      try {
        scenario.equipment = decodeDecimal128Deep(scenario.equipment);
        scenario.sensorCorrelations = decodeDecimal128Deep(scenario.sensorCorrelations);
        scenario.equipmentInteractions = decodeDecimal128Deep(scenario.equipmentInteractions);
      } catch (decodeError) {
        console.error('Decimal128 decoding error for scenario:', scenario.name, decodeError);
      }

      return scenario;
    }).filter((s: SimulationScenario) => s.name);

    console.log(`[listScenarios] Returning ${scenarios.length} scenarios:`, scenarios.map(s => s.name));
    return scenarios;
  } catch (e) {
    console.warn('listScenarios failed', e);
    return [];
  }
}

export async function addScenario(collection: ScenariosCollection, scenario: SimulationScenario, ctx: PlatformContext): Promise<void> {
  if (!collection?._userItemId) throw new Error('Scenarios collection not initialized');
  const now = new Date().toISOString();
  const row = {
    ...scenario,
    name: scenario.name,
    createdAt: now,
    updatedAt: now,
  };
  await IafItemSvc.createRelatedItemsBulk(collection._userItemId, [row], ctx);
}

export async function updateScenario(collection: ScenariosCollection, scenarioId: string, scenario: SimulationScenario, ctx: PlatformContext): Promise<void> {
  if (!collection?._userItemId) throw new Error('Scenarios collection not initialized');
  const now = new Date().toISOString();
  const row = {
    _id: scenarioId,
    ...scenario,
    name: scenario.name,
    updatedAt: now,
  };
  await IafItemSvc.updateRelatedItems(collection._userItemId, [row], ctx);
}

export async function deleteScenario(collection: ScenariosCollection, scenarioId: string, ctx: PlatformContext): Promise<void> {
  if (!collection?._userItemId) throw new Error('Scenarios collection not initialized');
  await IafItemSvc.deleteRelatedItems(collection._userItemId, [scenarioId], ctx);
}

export function deriveScenarioFromSeed(): SimulationScenario | null {
  try {
    const root = bimEquipment as Record<string, any[]>;
    const equipment = [];

    // Build equipment instances from seed data
    for (const [category, items] of Object.entries(root)) {
      if (!Array.isArray(items)) continue;

      for (const item of items) {
        const typeName: string = item?.equipment || item?.type || '';
        if (!typeName) continue;

        const typeCode = typeName
          .toUpperCase()
          .replace(/[^A-Z0-9]+/g, '_')
          .replace(/_+/g, '_')
          .replace(/^_+|_+$/g, '');

        // Create a basic equipment instance
        equipment.push({
          id: `${typeCode}-1`,
          equipmentTypeCode: typeCode,
          equipmentTypeName: typeName,
          category,
          powerProfile: {
            low: 0,
            medium: 0,
            high: 0,
            unit: 'kW',
            powerLoadFactor: 0.5,
            annualEnergyConsumption: {
              low: 0,
              medium: 0,
              high: 0,
              unit: 'kWh/year'
            }
          },
          dataPoints: [],
          anomalyConfig: {
            mode: 'probability' as const,
            probability: 0.01,
            enabled: false
          }
        });
      }
    }

    if (equipment.length === 0) return null;

    return {
      name: 'Default Building System',
      description: 'Auto-generated scenario from equipment seed data',
      equipment,
      sensorCorrelations: [],
      equipmentInteractions: []
    };
  } catch (e) {
    console.warn('deriveScenarioFromSeed failed', e);
    return null;
  }
}

export async function seedScenarioFromJson(collection: ScenariosCollection, ctx: PlatformContext): Promise<number> {
  if (!collection?._userItemId) return 0;

  // Get existing scenarios
  const existing = await listScenarios(collection, ctx);

  // Seed test scenario 1 (AHU + Chiller)
  const scenario1 = testScenario1 as any as SimulationScenario;
  const exists1 = existing.some(s => s.name === scenario1.name);
  if (!exists1) {
    await addScenario(collection, scenario1, ctx);
  }

  // Seed test scenario 2 (Building Systems)
  const scenario2 = testScenario2 as any as SimulationScenario;
  const exists2 = existing.some(s => s.name === scenario2.name);
  if (!exists2) {
    await addScenario(collection, scenario2, ctx);
  }

  // Count how many were added
  const added = (exists1 ? 0 : 1) + (exists2 ? 0 : 1);
  return added;
}
